/**
 * DealerLogoInlinePrompt — Jun-2026 (User-Wunsch).
 *
 * Zeigt Händlern OHNE hinterlegtes Profil-Logo einen kompakten Inline-Hinweis
 * mit Direkt-Upload. Das hochgeladene Logo wird sofort als `company_logo_url`
 * im Händlerprofil gespeichert → gebrandete PDFs (Preisschild, Angebot,
 * Verträge) zeigen ab sofort das Logo. Erscheint nur bei Rolle b2b ohne Logo.
 */
import { useEffect, useState } from "react";
import { ImagePlus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function DealerLogoInlinePrompt() {
    const [show, setShow] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/auth/me")
            .then((r) => {
                const u = r.data || {};
                const hasLogo = u.company_logo_url || u.logo_url || u.dealer_logo_url;
                if (active && (u.role === "b2b" || u.role === "admin") && !hasLogo) setShow(true);
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/users/me/dealer-profile", { company_logo_url: r.data.url });
            setDone(true);
            toast.success("Logo gespeichert — erscheint ab sofort auf Preisschild & Angeboten");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Logo-Upload fehlgeschlagen");
        } finally { setUploading(false); }
    };

    if (!show || done) return null;

    return (
        <div className="mb-3 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="dealer-logo-inline-prompt">
            <ImagePlus className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-[12px] text-amber-900 flex-1 min-w-[180px]">
                Kein Händler-Logo hinterlegt — für gebrandete PDFs (Preisschild, Angebot) jetzt direkt hochladen.
            </span>
            <label className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-white cursor-pointer shrink-0" data-testid="dealer-logo-inline-upload-label">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt…</> : <><ImagePlus className="w-3.5 h-3.5" /> Logo hochladen</>}
                <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} data-testid="dealer-logo-inline-upload-input" />
            </label>
        </div>
    );
}
